import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft, faChevronRight, faCalendarDay
} from '@fortawesome/free-solid-svg-icons';
import './HeartFillComponent.css';

// Helper Functions
const formatShortDate = d => {
  if (!d || !(d instanceof Date || typeof d === 'string' || typeof d === 'number')) return 'N/A';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  catch (e) { console.error("Error formatting date:", d, e); return 'N/A'; }
};
const calculateCaloriesFromSteps = (steps = 0) => Math.round(steps * 0.04);
const calculateDurationFromSteps = (steps = 0) => Math.round(steps * 0.01);

// Reversed Paths & Center Point
const OUTER_HEART_D_REV = "M 50,20 C 80,5 125,55 50,100 C -25,55 20,5 50,20";
const MIDDLE_HEART_D_REV = "M 50,25 C 80,5 125,52.25 50,95 C -25,52.25 20,5 50,25";
const INNER_HEART_D_REV = "M 50,32 C 80,5 125,49.5 50,90 C -25,49.5 20,5 50,32";
const VISUAL_CENTER_X = 50;
const VISUAL_CENTER_Y = 55; // Adjusted Y center

// Default Goals
const DEFAULT_GOALS = {
  steps: 10000,
  calories: 500,
  duration: 60,
};

// Refined HeartRing Component
// --- HeartRing (replace the whole function) ---
const HeartRing = ({
    d,
    progress,
    color,
    strokeWidth,
    progressStrokeWidth,
    backgroundOpacity = 0.2,
    transform = '',
  }) => {
    const pct = Math.max(0, Math.min(progress, 1)) * 100;  // 0‑100 %
  
    return (
      <g transform={transform}>
        {/* background */}
        <path
          d={d}
          fill="none"
          pathLength="100"
          stroke={color}
          strokeWidth={strokeWidth}
          opacity={backgroundOpacity}
          strokeLinecap="round"
        />
        {/* progress */}
        <path
          d={d}
          fill="none"
          pathLength="100"
          stroke={color}
          strokeWidth={progressStrokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${pct} 100`}          // always accurate, any scale
          style={{ transition: 'stroke-dasharray .5s ease-out' }}
        />
      </g>
    );
  };
  


// SVG Indicator Component using HeartRing
const DaHeartProgressIndicatorSVG = ({
    progressOuter,
    progressMiddle,
    progressInner,
    colors,
    size = 60
}) => {
  const baseStrokeWidth = size * 0.08;
  const baseProgressStrokeWidth = size * 0.15;
  const innerProgressStrokeWidth = size * 0.2;

  const cx = VISUAL_CENTER_X;
  const cy = VISUAL_CENTER_Y;

  const outerTransform = "";
  const middleScale = 0.75; 
  const middleTransform = `translate(${cx}, ${cy}) scale(${middleScale}) translate(${-cx}, ${-cy})`;
  const innerScale = 0.5;
  const innerTransform = `translate(${cx}, ${cy}) scale(${innerScale}) translate(${-cx}, ${-cy})`;


return (
    <svg viewBox="-30 0 160 120" style={{ width: '100%', height: '100%', display: 'block' }}>
      <g transform={`translate(${VISUAL_CENTER_X},${VISUAL_CENTER_Y}) scale(1.2) translate(${-VISUAL_CENTER_X},${-VISUAL_CENTER_Y})`}>
          <HeartRing
            d={OUTER_HEART_D_REV}
            id="outer-heart"
            progress={progressOuter}
            color={colors.outer}
            strokeWidth={baseStrokeWidth}
            progressStrokeWidth={baseProgressStrokeWidth}
            transform=""          // unchanged
          />
        <HeartRing
          d={MIDDLE_HEART_D_REV}
          id="middle-heart"
          progress={progressMiddle}
          color={colors.middle}
          strokeWidth={baseStrokeWidth}
          progressStrokeWidth={baseProgressStrokeWidth}
          transform={middleTransform}
        />
        <HeartRing
          d={INNER_HEART_D_REV}
          id="inner-heart"
          progress={progressInner}
          color={colors.inner}
          strokeWidth={baseStrokeWidth}
          progressStrokeWidth={innerProgressStrokeWidth}
          transform={innerTransform}
        />
      </g>
    </svg>
  );
  
};

// Main Component Wrapper
export default function HeartFillComponent({
  stepsData = [],
  goals = DEFAULT_GOALS
}) {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const ringColors = useMemo(() => ({
    outer: '#74C69D',
    middle: '#4D96FF',
    inner: '#B57EDC'
  }), []);

  useEffect(() => {
    if (stepsData && stepsData.length > 0) {
      const newIndex = stepsData.length - 1;
      if (selectedIndex < 0 || selectedIndex >= stepsData.length) { setSelectedIndex(newIndex); }
    } else { setSelectedIndex(-1); }
  }, [stepsData]);

  const currentDayData = useMemo(() => {
    if (stepsData && selectedIndex >= 0 && selectedIndex < stepsData.length) {
      return stepsData[selectedIndex];
    }
    return null;
  }, [selectedIndex, stepsData]);

  const isLoading = selectedIndex < 0 && stepsData?.length > 0;
  const noDataAvailable = stepsData.length === 0;
  const noDataForDay = !isLoading && !noDataAvailable && !currentDayData;

  const currentSteps = !isLoading && !noDataAvailable && currentDayData ? (currentDayData.steps || 0) : 0;
  const activeTime = calculateDurationFromSteps(currentSteps);
  const calories = calculateCaloriesFromSteps(currentSteps);

  const calculateProgress = (value, goal) => (goal > 0 ? Math.max(0, Math.min(1, value / goal)) : 0);

  const stepsProgress = calculateProgress(currentSteps, goals.steps);
  const activeTimeProgress = calculateProgress(activeTime, goals.duration);
  const caloriesProgress = calculateProgress(calories, goals.calories);

  const handlePrevDay = () => setSelectedIndex(prev => Math.max(0, prev - 1));
  const handleNextDay = () => {
    const maxIndex = stepsData ? stepsData.length - 1 : 0;
    setSelectedIndex(prev => Math.min(maxIndex, prev + 1));
  };
  const disableNav = isLoading || noDataAvailable;
  const canGoPrev = !disableNav && selectedIndex > 0;
  const canGoNext = !disableNav && stepsData && selectedIndex < stepsData.length - 1;

  let statusMessage = '';
  if (isLoading) { statusMessage = "Loading Data..."; }
  else if (noDataAvailable) { statusMessage = " "; }
  else if (noDataForDay) { statusMessage = "No Activity Data"; }


  // Early return with just the 'No data' message when there's no data available
  if (statusMessage) {
    return <div className="APM-heart-svg-container APM-no-data">{statusMessage}</div>;
  }

  return (
    <div className="HeartFillComponent APM-heart-svg-container">
       <div className="APM-heart-date-navigation">
           <button className="APM-month-nav-btn" onClick={handlePrevDay} disabled={!canGoPrev} aria-label="Previous Day"><FontAwesomeIcon icon={faChevronLeft} /></button>
           <div className="APM-heart-selected-date"><FontAwesomeIcon icon={faCalendarDay} /><span>{formatShortDate(currentDayData?.date)}</span></div>
           <button className="APM-month-nav-btn" onClick={handleNextDay} disabled={!canGoNext} aria-label="Next Day"><FontAwesomeIcon icon={faChevronRight} /></button>
       </div>

        <div className="APM-heart-svg-wrapper flutter-heart-style">
            <DaHeartProgressIndicatorSVG
                progressOuter={stepsProgress}
                progressMiddle={activeTimeProgress}
                progressInner={caloriesProgress}
                    colors={ringColors}
                />
        </div>

       <div className={`APM-heart-stats APM-heart-svg-stats ${statusMessage ? 'APM-heart-stats-empty' : ''}`}>
           {isLoading || noDataAvailable ? ( <div className="APM-no-data">{isLoading ? "Loading stats..." : "No stats available."}</div> )
           : noDataForDay ? ( <div className="APM-no-data">No stats for this day.</div> )
           :(
            <>
              <div className="APM-heart-stat-item steps">
                <div className="APM-heart-stat-label-row">
                   <span className="APM-heart-stat-labelh">Steps</span>
                </div>
                <div className="APM-heart-stat-valueh">
                <span className="APM-heart-stat-dot" style={{ backgroundColor: ringColors.outer }}></span>
                {currentSteps.toLocaleString()}</div>
                <div className="APM-heart-stat-goal">/{goals.steps.toLocaleString()}</div>
              </div>
              <div className="APM-heart-stat-item duration">
                <div className="APM-heart-stat-label-row">
                   <span className="APM-heart-stat-labelh">Active time</span>
                </div>
                <div className="APM-heart-stat-valueh">                   <span className="APM-heart-stat-dot" style={{ backgroundColor: ringColors.middle }}></span>
                {activeTime}</div>
                <div className="APM-heart-stat-goal">/{goals.duration} mins</div>
              </div>
              <div className="APM-heart-stat-item calories">
                 <div className="APM-heart-stat-label-row">
                    <span className="APM-heart-stat-labelh">Activity Cal</span>
                 </div>
                <div className="APM-heart-stat-valueh">                    <span className="APM-heart-stat-dot" style={{ backgroundColor: ringColors.inner }}></span>
                {calories.toLocaleString()}</div>
                <div className="APM-heart-stat-goal">/{goals.calories} Cal</div>
              </div>
            </>
            )}
       </div>
    </div>
  );
}